// Anonymous playtester analytics -- captures which rune combinations get
// used, how far players get, and where they get stuck, so a shared build can
// answer "which combos does nobody find" / "where do people quit" without
// any real identity attached.
//
// Gated entirely on !getDevMode() (settings.ts) -- the same toggle Developer
// Tools' rune-gate-bypass already uses. Developer Tools itself is hidden
// behind a secret gesture now (see ui.ts's initUI), so dev mode is
// realistically only ever on during the developer's own testing -- a clean,
// already-existing signal for "this session isn't a real playtester,"
// checked fresh on every trackEvent call rather than needing a separate
// switch or a manual export step afterward.
//
// Batched client-side and flushed periodically (not one HTTP request per
// event) plus reliably on tab-close via sendBeacon. Server-side dedup is by
// eventId (see analyticsApi.ts's INSERT OR IGNORE), so a resent batch after a
// flaky response can never double-count.
import { getDevMode } from './settings';
import { onNewGame } from './gameReset';

const PLAYER_ID_KEY = 'rune-world-game-player-id';
const RUN_ID_KEY = 'rune-world-game-run-id';
const ENDPOINT = '/api/analytics';
const FLUSH_INTERVAL_MS = 10_000;
const MAX_QUEUE_BEFORE_FORCE_FLUSH = 20;

function loadOrCreate(key: string): string {
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const created = crypto.randomUUID();
  localStorage.setItem(key, created);
  return created;
}

// playerId is a persistent anonymous id per browser (never reset, not even
// by New Game) -- lets you tell a repeat tester's several runs apart from
// several different people. runId is per New Game (including the very first
// life); resuming a saved game deliberately keeps the same runId, since
// loadOrCreate() above just restores whatever was already persisted.
const playerId = loadOrCreate(PLAYER_ID_KEY);
let runId = loadOrCreate(RUN_ID_KEY);

interface QueuedEvent {
  eventId: string;
  playerId: string;
  runId: string;
  eventType: string;
  payload: Record<string, unknown>;
}

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

export function trackEvent(eventType: string, payload: Record<string, unknown> = {}): void {
  if (getDevMode()) return;
  queue.push({ eventId: crypto.randomUUID(), playerId, runId, eventType, payload });
  if (queue.length >= MAX_QUEUE_BEFORE_FORCE_FLUSH) flush();
  if (flushTimer === null) flushTimer = setInterval(() => flush(), FLUSH_INTERVAL_MS);
}

function flush(useBeacon = false): void {
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  const body = JSON.stringify({ events: batch });
  if (useBeacon && navigator.sendBeacon) {
    navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
    return;
  }
  fetch(ENDPOINT, { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true }).catch(() => {
    // Best-effort -- a dropped analytics batch is never worth retrying
    // indefinitely or surfacing to the player.
  });
}

// Send whatever is queued right now instead of waiting for the 10s timer.
// Exists for errorTelemetry.ts: a crash may be immediately followed by the tab
// dying or the player closing it, so a crash report has no later batch to ride
// along with the way an ordinary gameplay event does.
export function flushAnalyticsNow(): void {
  flush();
}

// The player id feedback.ts attaches to a submission, so a piece of feedback
// can be cross-referenced against that same person's analytics trail.
export function getAnalyticsPlayerId(): string {
  return playerId;
}

// Wired once at boot (see GameScene's create()). Deliberately doesn't fire
// its own session_start immediately -- every playthrough, first-time or
// returning, funnels through the title screen's New Game button at least
// once (there's no other way into a fresh life), which triggers onNewGame
// below; firing one here too would double-count every single page load.
// Resuming a saved game intentionally does NOT go through onNewGame, so it
// correctly keeps accumulating events under whatever runId was persisted,
// not a new one.
export function initAnalytics(): void {
  onNewGame(() => {
    runId = crypto.randomUUID();
    localStorage.setItem(RUN_ID_KEY, runId);
    trackEvent('session_start');
  });
  // visibilitychange fires on document, not window (it doesn't bubble) --
  // attaching this to window would silently never fire.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
  window.addEventListener('pagehide', () => flush(true));
}
